import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  BackgroundCheckApiResponse,
  GetApplicationStatus,
  VddOnboardingRecord,
  decodeGetApplicationStatus,
  decodeVddOnboardingRecord,
} from './vendors-service.interface';

@Injectable({
  providedIn: 'root',
})
export class VendorsService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getVendors(page = 1, showSubmitted = false, searchQuery?: string) {
    const query = {
      page,
      showSubmitted,
      ...(searchQuery && { q: searchQuery }),
    };

    let queryParams = new HttpParams();

    for (const [key, value] of Object.entries(query)) {
      queryParams = queryParams.set(key, value.toString());
    }

    return this.http
      .get<{
        vendors: VddOnboardingRecord[];
        pagination: {
          page: number;
          limit: number;
          totalCount: number;
          totalPages: number;
        };
      }>(`${this.apiUrl}v1/vendor/list`, { params: queryParams })
      .pipe(
        map((response) => ({
          vendors: response.vendors.map((vendor) => decodeVddOnboardingRecord(vendor)),
          pagination: response.pagination,
        })),
      );
  }

  getApplicationStatus(id: number) {
    return this.http
      .get<GetApplicationStatus>(`${this.apiUrl}v1/vendor/${id}/status`)
      .pipe(map((data) => decodeGetApplicationStatus(data)));
  }

  runRulesForOwner(onboardingId: number, ownerId: number, apiNames: string[]) {
    return this.http.post<void>(`${this.apiUrl}v1/vendor/${onboardingId}/owners/run-background-checks`, {
      ownerId,
      apiNames,
    });
  }

  getBackgroundChecksForOwners(onboardingId: number) {
    return this.http
      .get<{ background_checks: BackgroundCheckApiResponse[] }>(
        `${this.apiUrl}v1/vendor/${onboardingId}/owners/background-checks`,
      )
      .pipe(
        map(({ background_checks }) =>
          background_checks.map((check) => ({
            ...check,
            createdOn: new Date(check.createdOn),
          })),
        ),
      );
  }

  runRulesForBusiness(onboardingId: number, apiNames: string[]) {
    return this.http.post<void>(`${this.apiUrl}v1/vendor/${onboardingId}/business/run-background-checks`, {
      apiNames,
    });
  }

  getBackgroundChecksForBusiness(onboardingId: number) {
    return this.http
      .get<{ background_checks: BackgroundCheckApiResponse[] }>(
        `${this.apiUrl}v1/vendor/${onboardingId}/business/background-checks`,
      )
      .pipe(
        map(({ background_checks }) =>
          background_checks.map((check) => ({
            ...check,
            createdOn: new Date(check.createdOn),
          })),
        ),
      );
  }

  saveExtraOwners(onboardingId: number, extraOwners: { email: string; name: string }[]) {
    return this.http.post<void>(`${this.apiUrl}v1/vendor/${onboardingId}/owners/save-extra-owners`, {
      extraOwners,
    });
  }

  approveVendor(id: number) {
    return this.http.patch<void>(`${this.apiUrl}v1/vendor/${id}/approve`, {});
  }

  cancelVendor(id: number) {
    return this.http.patch<void>(`${this.apiUrl}v1/vendor/${id}/cancel`, {});
  }

  submitVendor(id: number, comment: string) {
    return this.http.patch<void>(`${this.apiUrl}v1/vendor/${id}/submit`, { comment });
  }

  archiveVendor(id: number) {
    return this.http.patch<void>(`${this.apiUrl}v1/vendor/${id}/archive`, {});
  }

  unarchiveVendor(id: number) {
    return this.http.patch<void>(`${this.apiUrl}v1/vendor/${id}/unarchive`, {});
  }

  getVendorFileDownloadUrl(vendorId: number, fieldName: string) {
    return this.http
      .get<{ presigned_url: string }>(`${this.apiUrl}v1/vendor/${vendorId}/file/${fieldName}`)
      .pipe(map((res) => res.presigned_url));
  }

  downloadSignedDocument(documentId: number): void {
    this.http
      .get(`${this.apiUrl}documenso/download/${documentId}`, { responseType: 'blob' })
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `signed_document_${documentId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  openSignedDocumentInTab(documentId: number): void {
    this.http
      .get(`${this.apiUrl}documenso/download/${documentId}`, { responseType: 'blob' })
      .subscribe((blob) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      });
  }
}
